import { useCallback, useEffect, useRef, useState } from 'react';
import CodeTool from '@editorjs/code';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import EditorjsList from '@editorjs/list';
import Marker from '@editorjs/marker';
import { Menu, MenuContent, MenuTrigger } from '@optiaxiom/react';

import { styles } from './EditorComponent.css';
import { useEditorConverters } from './useEditorConverters';

type Selectable = {
  name: string;
  description?: string | null;
};

type EditorComponentProps = {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  selectables?: Selectable[];
};

export const EditorComponent = ({
  value = '',
  onChange,
  placeholder = 'Enter your prompt template',
  className,
  selectables = [],
}: EditorComponentProps) => {
  const editorRef = useRef<EditorJS | null>(null);
  const holderRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const currentTargetRef = useRef<HTMLElement | null>(null);

  const { debouncedConvertBlocksToMarkdown, parseValueToEditorData } = useEditorConverters();

  useEffect(() => {
    onChangeRef.current = onChange;
    valueRef.current = value;
  }, [onChange, value]);

  const insertSelectable = useCallback((selectable: Selectable) => {
    const targetElement = currentTargetRef.current;
    if (!targetElement) return;

    const text = targetElement.innerText;
    const lastSlashIndex = text.lastIndexOf('/');

    if (lastSlashIndex !== -1) {
      // Create the selectable element with a non-breaking space after it
      const selectableElement = document.createElement('mark');
      selectableElement.className = 'cdx-marker';
      selectableElement.textContent = `[[${selectable.name}]]`;

      // Add a zero-width space after the marker to create a boundary
      const boundaryNode = document.createTextNode('\u200B'); // Zero-width space

      // Create a document fragment to insert both elements atomically
      const fragment = document.createDocumentFragment();
      fragment.appendChild(selectableElement);
      fragment.appendChild(boundaryNode);

      // Create a new range that starts from the slash position
      const range = document.createRange();

      // Find the text node containing the slash
      const walker = document.createTreeWalker(targetElement, NodeFilter.SHOW_TEXT, null);

      let textNode = walker.nextNode();
      let accumulatedLength = 0;

      while (textNode) {
        const nodeLength = textNode.textContent?.length || 0;
        if (accumulatedLength + nodeLength > lastSlashIndex) {
          // This text node contains the slash
          const offsetInNode = lastSlashIndex - accumulatedLength;
          range.setStart(textNode, offsetInNode);
          range.setEnd(textNode, offsetInNode + 1); // Select just the slash
          break;
        }
        accumulatedLength += nodeLength;
        textNode = walker.nextNode();
      }

      // Delete the slash and replace with the fragment
      range.deleteContents();
      range.insertNode(fragment);

      // Position cursor after the boundary node
      setTimeout(() => {
        const selection = window.getSelection();
        if (selection && targetElement) {
          range.setStartAfter(boundaryNode);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }, 10);
    }

    setMenuOpen(false);
  }, []);

  const showSelectableMenu = useCallback(
    (targetElement: HTMLElement) => {
      if (selectables.length === 0) return;

      // Don't show dropdown if EditorJS toolbox is visible
      if (document.querySelector('.codex-editor--toolbox-opened')) {
        return;
      }

      currentTargetRef.current = targetElement;

      // Get the current cursor position
      const selection = window.getSelection();
      let cursorRect;

      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        cursorRect = range.getBoundingClientRect();
      } else {
        // Fallback to element position if no selection
        cursorRect = targetElement.getBoundingClientRect();
      }

      setMenuPosition({
        top: cursorRect.top + window.scrollY - 5,
        left: cursorRect.right + window.scrollX + 5,
      });

      setMenuOpen(true);
    },
    [selectables]
  );

  useEffect(() => {
    if (!holderRef.current || editorRef.current) return;

    const currentHolder = holderRef.current;

    const initializeEditor = async () => {
      try {
        const initialData = parseValueToEditorData(valueRef.current);

        editorRef.current = new EditorJS({
          holder: currentHolder,
          placeholder,
          tools: {
            header: Header,
            code: CodeTool,
            list: EditorjsList,
            marker: {
              class: Marker,
              shortcut: 'CMD+SHIFT+M',
            },
          },
          data: initialData,
          onChange: async () => {
            if (editorRef.current && onChangeRef.current) {
              try {
                const outputData = await editorRef.current.save();
                debouncedConvertBlocksToMarkdown(outputData.blocks, markdown => {
                  onChangeRef.current?.(markdown);
                });
              } catch (error) {
                console.error('Error saving editor data:', error);
              }
            }
          },
          minHeight: 100,
        });

        await editorRef.current.isReady;
      } catch (error) {
        console.error('Error initializing EditorJS:', error);
        editorRef.current = null;
      }
    };

    initializeEditor();

    return () => {
      if (editorRef.current && editorRef.current.destroy) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, [placeholder, debouncedConvertBlocksToMarkdown, parseValueToEditorData]);

  useEffect(() => {
    if (!holderRef.current) return;

    const currentHolder = holderRef.current;

    const keydownHandler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;

      if (event.key === '/' && target && target.isContentEditable) {
        setTimeout(() => {
          if (target && target.isContentEditable) {
            showSelectableMenu(target);
          }
        }, 10);
      }
    };

    currentHolder.addEventListener('keydown', keydownHandler, true);

    return () => {
      currentHolder.removeEventListener('keydown', keydownHandler, true);
    };
  }, [showSelectableMenu]);

  return (
    <>
      <div ref={holderRef} className={`${className} ${styles.editorContainer}`} />
      {menuOpen && (
        <div
          style={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
            zIndex: 1000,
          }}
        >
          <Menu
            open={menuOpen}
            onOpenChange={setMenuOpen}
            options={selectables.map(selectable => ({
              label: selectable.name,
              description: selectable.description || undefined,
              execute: () => insertSelectable(selectable),
            }))}
          >
            <MenuTrigger>Select</MenuTrigger>
            <MenuContent />
          </Menu>
        </div>
      )}
    </>
  );
};
